import { useState } from "react";
import { Search } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Input } from "@/components/ui/input";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const HelpCenter = () => {
  const [searchQuery, setSearchQuery] = useState("");

  const faqs = [
    {
      question: "O que é a EscriturIA?",
      answer: "A EscriturIA é uma plataforma de inteligência artificial desenvolvida especificamente para pastores, pregadores e líderes cristãos. Ela auxilia na preparação de sermões, estudos bíblicos e mensagens, combinando teologia sólida com tecnologia de ponta."
    },
    {
      question: "Como funciona a EscriturIA?",
      answer: "Nossa plataforma utiliza inteligência artificial treinada em recursos teológicos e exegéticos para ajudá-lo a pesquisar, organizar e desenvolver conteúdo bíblico. Você pode gerar esboços de sermões, análises exegéticas, ilustrações e aplicações práticas da Palavra."
    },
    {
      question: "A EscriturIA substitui o estudo pessoal da Bíblia?",
      answer: "Não! A EscriturIA é uma ferramenta de auxílio que complementa seu estudo pessoal. Ela acelera processos de pesquisa e organização, mas a interpretação final, a direção do Espírito Santo e a aplicação pastoral continuam sendo sua responsabilidade."
    },
    {
      question: "Qual a diferença entre os planos?",
      answer: "Os planos variam em recursos e limites de uso. O Plano ALEF é gratuito com recursos básicos. Os planos pagos (BEIT, GUIMEL e DALET) oferecem mais gerações mensais, recursos avançados de exegese, análise em múltiplos idiomas e suporte prioritário."
    },
    {
      question: "A teologia da EscriturIA é confiável?",
      answer: "Sim! Nossa IA foi desenvolvida com base em recursos teológicos evangélicos reconhecidos e passa por constante revisão teológica. No entanto, recomendamos que você sempre revise o conteúdo gerado à luz das Escrituras e de sua tradição teológica."
    },
    {
      question: "Posso cancelar minha assinatura a qualquer momento?",
      answer: "Sim, você pode cancelar sua assinatura quando quiser, sem multas ou taxas adicionais. Seu acesso aos recursos premium permanecerá ativo até o final do período já pago."
    },
    {
      question: "A EscriturIA funciona em quais idiomas?",
      answer: "A plataforma está disponível em português, mas os planos superiores permitem análise exegética e geração de conteúdo em múltiplos idiomas, incluindo hebraico, grego, inglês e espanhol."
    },
    {
      question: "Como posso começar a usar?",
      answer: "É simples! Crie sua conta gratuitamente no Plano ALEF e comece a explorar a plataforma. Se precisar de mais recursos, você pode fazer upgrade para um plano pago a qualquer momento."
    },
    {
      question: "Como faço para criar uma conta?",
      answer: "Clique no botão 'Registrar-se' no topo da página e preencha suas informações. Você receberá um e-mail de confirmação para ativar sua conta."
    },
    {
      question: "Esqueci minha senha. O que faço?",
      answer: "Na tela de login, clique em 'Esqueci minha senha' e siga as instruções. Você receberá um e-mail com um link para redefinir sua senha."
    },
    {
      question: "Como atualizar meu plano?",
      answer: "Acesse 'Minha Conta' > 'Planos e Pagamentos' e selecione o plano desejado. O upgrade é instantâneo e você terá acesso imediato aos novos recursos."
    },
    {
      question: "Quais formas de pagamento são aceitas?",
      answer: "Aceitamos cartões de crédito (Visa, Mastercard, American Express), cartões de débito e PIX. Os pagamentos são processados de forma segura."
    },
    {
      question: "Como funciona o suporte técnico?",
      answer: "Usuários de planos pagos têm acesso a suporte prioritário via e-mail e chat. Usuários do plano gratuito podem enviar suas dúvidas por e-mail e receberão resposta em até 48 horas."
    },
    {
      question: "Meus dados estão seguros?",
      answer: "Sim! Utilizamos criptografia de ponta a ponta e seguimos as melhores práticas de segurança. Seus dados pessoais e conteúdos criados na plataforma são confidenciais e protegidos."
    },
    {
      question: "Posso usar a EscriturIA offline?",
      answer: "Não, a EscriturIA é uma plataforma online que requer conexão com a internet para funcionar. Entretanto, você pode exportar e salvar seus sermões e estudos para acesso offline."
    },
    {
      question: "Como exportar meus sermões?",
      answer: "Todos os conteúdos criados podem ser exportados em formatos PDF, Word (.docx) e texto simples (.txt). Basta clicar no botão 'Exportar' no editor de sermões."
    }
  ];

  const filteredFaqs = faqs.filter(faq =>
    faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
    faq.answer.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      
      <main className="flex-1 pt-20">
        {/* Hero Section */}
        <section className="py-16 bg-primary text-primary-foreground">
          <div className="container mx-auto px-4 text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              Central de Ajuda
            </h1>
            <p className="text-xl text-primary-foreground/80 mb-8 max-w-2xl mx-auto">
              Encontre respostas para suas dúvidas sobre a EscriturIA
            </p>
            
            {/* Search Field */}
            <div className="max-w-2xl mx-auto relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-muted-foreground h-5 w-5" />
              <Input
                type="text"
                placeholder="Digite sua pergunta..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-12 py-6 text-lg bg-background text-foreground border-border"
              />
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-16 bg-muted/30">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-foreground">
                Perguntas Frequentes
              </h2>
              
              {filteredFaqs.length > 0 ? (
                <Accordion type="single" collapsible className="w-full space-y-4">
                  {filteredFaqs.map((faq, index) => (
                    <AccordionItem 
                      key={index} 
                      value={`item-${index}`}
                      className="bg-card border border-border rounded-lg px-4 hover:border-primary/50 transition-colors"
                    >
                      <AccordionTrigger className="text-left text-lg font-semibold hover:no-underline">
                        {faq.question}
                      </AccordionTrigger>
                      <AccordionContent className="text-muted-foreground text-base leading-relaxed pt-2">
                        {faq.answer}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              ) : (
                <div className="text-center py-12">
                  <p className="text-muted-foreground text-lg">
                    Nenhuma pergunta encontrada para "{searchQuery}"
                  </p>
                  <p className="text-muted-foreground mt-2">
                    Tente usar palavras-chave diferentes ou{" "}
                    <a 
                      href="mailto:suporteescrituria@gmail.com" 
                      className="text-primary hover:text-primary/80 underline"
                    >
                      entre em contato conosco
                    </a>
                  </p>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* Contact Support Section */}
        <section className="py-16 bg-background">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl font-bold mb-4 text-foreground">
              Não encontrou o que procurava?
            </h2>
            <p className="text-muted-foreground text-lg mb-8 max-w-2xl mx-auto">
              Nossa equipe de suporte está pronta para ajudar você
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="mailto:suporteescrituria@gmail.com"
                className="inline-flex items-center justify-center px-8 py-3 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors font-semibold"
              >
                Enviar E-mail
              </a>
              <a
                href="https://www.youtube.com/@falaescrituria"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center px-8 py-3 bg-secondary text-secondary-foreground rounded-lg hover:bg-secondary/90 transition-colors font-semibold"
              >
                Assistir Tutoriais
              </a>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default HelpCenter;
